import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  type EbvAverageData,
  type EbvConfigGroup,
  type EbvConfigItem,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { PRIMARY_BELOW_GAP_PX, STICKY_HEADER_DEFAULT } from './constants';
import { getMateSnapScrollTop } from './scrollSnap';
import type { MatingDisplayBy, MatingPredictorResponse } from '../types';
import { useMatingPredictorStore } from '@/publicSearch/store/useMatingPredictorStore';

interface UseDesktopMatingTableLayoutArgs {
  data: MatingPredictorResponse;
  ebvGroups: EbvConfigGroup[];
  visibleEbvs: EbvConfigItem[];
  ebvAverage: EbvAverageData | null;
  displayBy: MatingDisplayBy;
  currentPageIndex: number;
  safePageIndex: number;
  primaryGroupId: number | undefined;
  totalPages: number;
}

export function useDesktopMatingTableLayout({
  data,
  ebvGroups,
  visibleEbvs,
  ebvAverage,
  displayBy,
  currentPageIndex,
  safePageIndex,
  primaryGroupId,
  totalPages,
}: UseDesktopMatingTableLayoutArgs) {
  const setTableScrollTop = useMatingPredictorStore(
    (state) => state.setTableScrollTop,
  );

  /** Measured height of the full thead (category + trait header rows when present). */
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState<number>(
    STICKY_HEADER_DEFAULT,
  );
  /** Height of the category group row only; label row sticks at this offset. */
  const [categoryHeaderHeight, setCategoryHeaderHeight] = useState<number>(0);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const categoryHeaderRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    const thead = theadRef.current;
    if (!thead) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setStickyHeaderHeight(height);
      }
    });
    observer.observe(thead);
    return () => observer.disconnect();
  }, [ebvGroups.length, visibleEbvs.length]);

  useEffect(() => {
    if (ebvGroups.length === 0) return;
    const row = categoryHeaderRowRef.current;
    if (!row) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setCategoryHeaderHeight(height);
      }
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [ebvGroups.length, visibleEbvs.length]);

  const [breedAveragesRowHeight, setBreedAveragesRowHeight] =
    useState<number>(0);
  const breedAveragesRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!ebvAverage) {
      const raf = requestAnimationFrame(() => setBreedAveragesRowHeight(0));
      return () => cancelAnimationFrame(raf);
    }
    const row = breedAveragesRowRef.current;
    if (!row) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setBreedAveragesRowHeight(height);
      }
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [ebvAverage]);

  /** Top offset for the selected sire/dam (primary) row so it sits under header + optional breed averages. */
  const primaryStickyTop = stickyHeaderHeight + breedAveragesRowHeight;

  /** Measured height of the primary row, used with PRIMARY_BELOW_GAP_PX for secondary scroll snap. */
  const [primaryRowHeight, setPrimaryRowHeight] = useState<number>(40);
  const [primaryRowEl, setPrimaryRowEl] = useState<HTMLTableRowElement | null>(
    null,
  );
  /** Same node as {@link primaryRowEl}; updated synchronously in the ref callback so layout snap can read height in the same commit as the row mount. */
  const primaryRowDomRef = useRef<HTMLTableRowElement | null>(null);
  const primaryRowRef = useCallback((node: HTMLTableRowElement | null) => {
    primaryRowDomRef.current = node;
    setPrimaryRowEl(node);
  }, []);

  /** Prefer live DOM row heights; React state for sticky heights can lag one frame behind paint. */
  const getMeasuredSnapOffsetPx = useCallback((): number => {
    const getRowHeight = (
      rowElement: HTMLElement | null | undefined,
      fallback: number,
    ) =>
      rowElement != null && rowElement.offsetHeight > 0
        ? rowElement.offsetHeight
        : fallback;
    const headerHeight = getRowHeight(theadRef.current, stickyHeaderHeight);
    const breedAverageHeight = ebvAverage
      ? getRowHeight(breedAveragesRowRef.current, breedAveragesRowHeight)
      : 0;
    const primaryHeight = getRowHeight(
      primaryRowDomRef.current,
      primaryRowHeight,
    );
    return (
      headerHeight + breedAverageHeight + primaryHeight + PRIMARY_BELOW_GAP_PX
    );
  }, [
    ebvAverage,
    stickyHeaderHeight,
    breedAveragesRowHeight,
    primaryRowHeight,
  ]);

  useEffect(() => {
    const primaryRowElement = primaryRowEl;
    if (!primaryRowElement) return;
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const primaryRowMeasuredHeight =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setPrimaryRowHeight(primaryRowMeasuredHeight);
      }
    });
    resizeObserver.observe(primaryRowElement);
    return () => resizeObserver.disconnect();
  }, [primaryRowEl]);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollSaveRaf = useRef<number | null>(null);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapReleaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isSnappingRef = useRef<boolean>(false);

  /** Viewport width of the scroll container; pins inbreeding row content while EBVs scroll. */
  const [tableScrollClientWidth, setTableScrollClientWidth] =
    useState<number>(0);
  useLayoutEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;
    const syncTableScrollClientWidth = () =>
      setTableScrollClientWidth(tableContainer.clientWidth);
    syncTableScrollClientWidth();
    const resizeObserver = new ResizeObserver(syncTableScrollClientWidth);
    resizeObserver.observe(tableContainer);
    return () => resizeObserver.disconnect();
  }, []);

  const onResultsScroll = useCallback(() => {
    if (scrollSaveRaf.current != null) return;
    scrollSaveRaf.current = requestAnimationFrame(() => {
      scrollSaveRaf.current = null;
      const tableContainer = tableContainerRef.current;
      if (tableContainer) setTableScrollTop(tableContainer.scrollTop);
    });
  }, [setTableScrollTop]);

  useEffect(
    () => () => {
      if (scrollSaveRaf.current != null) {
        cancelAnimationFrame(scrollSaveRaf.current);
      }
    },
    [],
  );

  // Reset this table's scroll to the top whenever the page (selected sire/dam)
  // changes. The orchestrator owns the equivalent reset for the pivoted view's
  // own scroll container (only one of the two views is mounted at a time).
  const prevPageIndexRef = useRef<number | null>(null);
  useEffect(() => {
    const previousPageIndex = prevPageIndexRef.current;
    prevPageIndexRef.current = currentPageIndex;
    if (previousPageIndex === null || previousPageIndex === currentPageIndex)
      return;
    const tableContainer = tableContainerRef.current;
    if (tableContainer) tableContainer.scrollTop = 0;
    setTableScrollTop(0);
  }, [currentPageIndex, setTableScrollTop]);

  /**
   * After new results / measured sticky heights, restore persisted scroll or apply the same
   * mate-block snap used on scroll-end (instant) so the first group clears the sticky stack.
   * Re-runs when header / breed / primary heights change so late layout does not leave rows covered.
   */
  useLayoutEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer || primaryGroupId == null || totalPages < 1) return;

    const savedScrollTop = useMatingPredictorStore.getState().tableScrollTop;
    if (savedScrollTop > 0) {
      tableContainer.scrollTop = savedScrollTop;
      return;
    }

    const applyLayoutSnap = () => {
      const snapOffset = getMeasuredSnapOffsetPx();
      const targetScrollTop = getMateSnapScrollTop(
        tableContainer,
        snapOffset,
        tableContainer.scrollTop,
      );
      if (targetScrollTop == null) return;

      const scrollDelta = Math.abs(targetScrollTop - tableContainer.scrollTop);
      if (scrollDelta <= 1) return;
      if (tableContainer.scrollTop > 8 && scrollDelta > 48) return;

      tableContainer.scrollTop = targetScrollTop;
      setTableScrollTop(targetScrollTop);
    };

    applyLayoutSnap();
    // Sticky row heights / fonts can settle after the first layout pass; extra frames match
    // what the user sees after a tiny wheel scroll (scroll-end snap with fresh measurements).
    let secondSnapRaf = 0;
    const firstSnapRaf = requestAnimationFrame(() => {
      applyLayoutSnap();
      secondSnapRaf = requestAnimationFrame(() => applyLayoutSnap());
    });
    return () => {
      cancelAnimationFrame(firstSnapRaf);
      cancelAnimationFrame(secondSnapRaf);
    };
  }, [
    breedAveragesRowHeight,
    data.calculatedAt,
    displayBy,
    getMeasuredSnapOffsetPx,
    primaryGroupId,
    primaryRowHeight,
    safePageIndex,
    setTableScrollTop,
    stickyHeaderHeight,
    totalPages,
  ]);

  /**
   * After scrolling stops, snap the nearest secondary (dam/sire) row to sit just
   * below the sticky header + breed average + primary row + gap.
   */
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const snap = () => {
      if (isSnappingRef.current) return;
      const snapOffset = getMeasuredSnapOffsetPx();
      const targetScrollTop = getMateSnapScrollTop(
        container,
        snapOffset,
        container.scrollTop,
      );
      if (targetScrollTop == null) return;
      isSnappingRef.current = true;
      container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      if (snapReleaseTimeoutRef.current) {
        clearTimeout(snapReleaseTimeoutRef.current);
      }
      snapReleaseTimeoutRef.current = setTimeout(() => {
        isSnappingRef.current = false;
        snapReleaseTimeoutRef.current = null;
      }, 500);
    };

    const supportsScrollEnd = 'onscrollend' in HTMLElement.prototype;

    if (supportsScrollEnd) {
      container.addEventListener('scrollend', snap, { passive: true });
      return () => {
        container.removeEventListener('scrollend', snap);
        if (snapReleaseTimeoutRef.current) {
          clearTimeout(snapReleaseTimeoutRef.current);
          snapReleaseTimeoutRef.current = null;
        }
        isSnappingRef.current = false;
      };
    }

    const handleScroll = () => {
      if (isSnappingRef.current) return;
      if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
      snapTimeoutRef.current = setTimeout(snap, 250);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (snapTimeoutRef.current) {
        clearTimeout(snapTimeoutRef.current);
        snapTimeoutRef.current = null;
      }
      if (snapReleaseTimeoutRef.current) {
        clearTimeout(snapReleaseTimeoutRef.current);
        snapReleaseTimeoutRef.current = null;
      }
      isSnappingRef.current = false;
    };
  }, [
    breedAveragesRowHeight,
    data.calculatedAt,
    displayBy,
    getMeasuredSnapOffsetPx,
    primaryGroupId,
    primaryRowHeight,
    safePageIndex,
    stickyHeaderHeight,
  ]);

  return {
    theadRef,
    categoryHeaderRowRef,
    breedAveragesRowRef,
    tableContainerRef,
    primaryRowRef,
    stickyHeaderHeight,
    categoryHeaderHeight,
    primaryStickyTop,
    tableScrollClientWidth,
    onResultsScroll,
  };
}
